import { scaleLinear } from 'd3-scale'
import { Fragment, useEffect, useMemo, useRef, useState } from 'react'
import styled from 'styled-components'
import { useResizeObserver } from 'usehooks-ts'
import { useLabels } from 'Hooks/useLabels'
import { labelStyles } from 'components/MainComponent/LabelStyles'
import { nfp } from 'components/MainComponent/textBuilder'
const barHeight = 10
const leftMargin = 80
const rightMargin = 80
const labelMaxValue = 800

export const BarChart = ({
  userValue,
  thirdsData,
  rawData,
  healthLine,
  caffeineAverage,
  lang
}: {
  userValue: number
  healthLine: number | undefined
  thirdsData: { ranges: number[][]; average: number[]; median: number[] }
  rawData: { label: number; value: number }[]
  caffeineAverage: number
  lang: 'de' | 'fr' | undefined
}) => {
  const containerRef = useRef<HTMLDivElement>(null)
  const { width = 0, height = 0 } = useResizeObserver({
    ref: containerRef,
    box: 'border-box'
  })
  const Labels = useLabels(labelStyles, 'svg')
  const [data, setData] = useState(rawData.filter(m => !isNaN(m.label)))
  const [countOverMaximum, setCountOverMaximum] = useState(0)
  const [countAbstinent, setCountAbstinent] = useState(0)
  const [barOffsetTop, setBarOffsetTop] = useState(0)

  const [tooltipContent, setTooltipContent] = useState<
    | {
        label: number
        value: number
      }
    | undefined
  >()

  let maxData = data.map(m => m.value).reduce((a, b) => (a > b ? a : b), 0)
  let maxLabel = Math.min(
    labelMaxValue,
    Math.round(
      Math.max(
        data.map(m => m.label).reduce((a, b) => (a > b ? a : b), 0),
        Math.round((healthLine || 10) / 100) * 100
      ) / 100
    ) * 100
  )

  let potency = +[...Array(maxData.toString().length)]
    .map((_, i) => (i === 0 ? 1 : 0))
    .join('')

  let max = Math.max(10, maxData) // Math.round(maxData / potency) * potency
  let horizontalTicks = [
    ...Array(Math.min(10, Math.round(max / potency + 1)))
  ].map((m, i) => {
    return i * Math.round(potency)
  })

  let x = useMemo(() => {
    return scaleLinear()
      .domain([0, max])
      .range([0, width - leftMargin - rightMargin])
  }, [max, width])

  let yRangeExtent = [60, 380]
  let yDomainExtent = [Math.max(healthLine || 0, maxLabel), 0]

  let barHeight = Math.floor(
    (yRangeExtent[1] - yRangeExtent[0]) / ((maxLabel - 20) / 20)
  )

  let dataAverage = +(
    data.map(m => m.value * m.label).reduce((a, b) => a + b * 2, 0) /
    data.map(m => m.value).reduce((a, b) => a + b * 2, 0)
  ).toFixed(1)

  let y = useMemo(() => {
    return scaleLinear().domain(yDomainExtent).range(yRangeExtent)
  }, [data.length, maxLabel])

  const dataWithUserValue = useMemo(() => {
    const roundedUserValue = roundToNearest(userValue, 20)
    const existingBarIndex = data.findIndex(
      bar => bar.label === roundedUserValue
    )
    const displayIncrement = 0.5

    if (existingBarIndex === -1) {
      return [...data, { label: roundedUserValue, value: displayIncrement }]
    }

    return data.map((bar, index) =>
      index === existingBarIndex
        ? { ...bar, value: bar.value + displayIncrement }
        : bar
    )
  }, [data, userValue])

  useEffect(() => {
    if (!rawData) return
    let zero = rawData.filter(f => f.label === 0)[0]
    let groupedData: { [key: string]: { label: number; value: number }[] } = {}
    let roundedLabels = rawData
      .filter(f => f.value > 0 && f.value <= maxLabel)
      .map(m => {
        let roundedLabel = Math.floor(m.label / 20) * 20
        if (!groupedData[roundedLabel + '']) groupedData[roundedLabel + ''] = []
        groupedData[roundedLabel + ''].push(m)
        return { label: Math.round(m.label / 20) * 20, value: m.value }
      })
    let roundedData = Object.entries(groupedData).map(m => ({
      label: +m[0],
      value: m[1].map(l => l.value).reduce((a, b) => a + b, 0)
    }))
    setData(roundedData)

    if (zero) {
      setCountAbstinent(zero.value)
    }
    let count = rawData
      .filter(f => f.label >= maxLabel)
      .reduce((a, b) => a + b.value, 0)

    if (count > 0) setBarOffsetTop(20)
    setCountOverMaximum(count)
  }, [rawData])

  useEffect(() => {}, [countOverMaximum])

  function roundToNearest (value: number, nearest: number) {
    return Math.round(value / nearest) * nearest
  }

  console.log('///', {
    data,
    userValue,
    rounded: roundToNearest(userValue, 20),
    caffeineAverage
  })
  return (
    <BarChartRoot ref={containerRef}>
      <svg width={'100%'} height={500}>
        <g transform={`translate(${leftMargin + 5}, ${barOffsetTop})`}>
          {/*
    
            Y AXIS
                
          */}
          <line
            x1={0}
            x2={0}
            y1={y.range()[0]}
            y2={y.range()[1]}
            stroke='var(--coffee-light)'
          />
          {/*

          XTICKS

          */}
          {horizontalTicks.map((guide, i) => {
            return (
              <Fragment key={'horiticks' + i}>
                {guide > 0 && (
                  <line
                    x1={x(guide)}
                    x2={x(guide)}
                    y1={y.range()[0]}
                    y2={y.range()[1] + barHeight}
                    stroke='var(--coffee-color_3)'
                    opacity={0.4}
                  />
                )}
                <text
                  textAnchor={
                    i === 0
                      ? 'start'
                      : i === horizontalTicks.length - 1
                      ? 'middle'
                      : 'middle'
                  }
                  x={x(guide)}
                  y={y.range()[1] + 30}
                  fill='var(--coffee-light)'
                >
                  <Labels axisTick>{guide}</Labels>
                </text>
              </Fragment>
            )
          })}
          {/*

          YTICKS

        */}

          {[...Array(Math.round(maxLabel / 100) + 1)]
            .map((m, i) => {
              return i * 100
            })
            .map((bar, i) => {
              if (bar > maxLabel) return null
              if (bar === maxLabel)
                return (
                  <Fragment key={'yticks' + i}>
                    {
                      <text
                        textAnchor='end'
                        fill='var(--coffee-light)'
                        y={y(bar) + barHeight}
                        x={-10}
                      >
                        <Labels axisTick>mg</Labels>
                      </text>
                    }
                  </Fragment>
                )
              return (
                <Fragment key={'yticks' + i}>
                  {
                    <text
                      textAnchor='end'
                      fill='var(--coffee-light)'
                      y={y(bar) + barHeight}
                      x={-10}
                    >
                      <Labels axisTick>
                        {i === 0
                          ? lang === 'de'
                            ? 'abstinent'
                            : 'Pas de café'
                          : bar}
                      </Labels>
                    </text>
                  }
                </Fragment>
              )
            })}
          {/*
          
        
          {/*
          
          AVERAGE LINE
        
                */}
          {caffeineAverage && (
            <g
              transform={`translate(${x(0)},${y(caffeineAverage - barHeight)})`}
            >
              <line
                x2={x(max) + 20}
                strokeWidth={2}
                stroke='var(--coffee-color)'
                strokeDasharray={'5 4'}
              />

              <g transform={`translate(${x(max) + 30},0)`}>
                <circle
                  r={7}
                  style={{
                    stroke: 'var(--coffee-color)',
                    fill: 'none',
                    strokeWidth: 2
                  }}
                />
                <line
                  x1={-8}
                  x2={8}
                  y1={8}
                  y2={-8}
                  style={{
                    stroke: 'var(--coffee-color)',
                    fill: 'none',
                    strokeWidth: 2
                  }}
                />
              </g>

              <AverageValue className='ave  ' x={x(max) + 16} y={30}>
                {nfp(Math.round(caffeineAverage))} mg
              </AverageValue>
            </g>
          )}
          {/* 

            Horizontal Axis Arrow
          
          */}
          <>
            <polygon
              transform={`translate(0,${y.range()[1] + 40})scale(.8)`}
              points='35.91 7.78 50 4 35.91 .22 35.91 3.5 0 3.5 0 4.5 35.91 4.5 35.91 7.78'
              fill='var(--coffee-light)'
            />
            <text x={0} y={y.range()[1] + 65} fill='var(--coffee-light)'>
              <Labels axisTick>
                {lang === 'de' ? 'Anzahl Personen' : 'Par personne'}
              </Labels>
            </text>
          </>

          {/* 

            Vertical Axis Arrow
          
          */}
          <polygon
            transform={`translate(-5,${y.range()[0] - 80})scale(.8)`}
            points='7.78 14.09 4 0 .22 14.09 3.5 14.09 3.5 32 4.5 32 4.5 14.09 7.78 14.09'
            fill='var(--coffee-light)'
          />
          <text
            textAnchor='start'
            x={10}
            y={y.range()[0] - 70}
            fill='var(--coffee-light)'
          >
            <Labels axisTick>{lang === 'de' ? 'tägliche' : 'par jour'}</Labels>
            <Labels axisTick x={10} dy={16}>
              {lang === 'de' ? 'Koffeinmenge' : 'Quantité de caféine'}
            </Labels>
          </text>

          {/* 
          
          BARS
          
          */}
          {dataWithUserValue
            .filter(f => f.label <= maxLabel)
            .map((bar, i) => {
              let userRound = Math.round(userValue / 20) * 20
              let barColor =
                userRound === bar.label
                  ? 'var(--orange-main)'
                  : bar.label < caffeineAverage
                  ? 'var(--coffee-color_2)'
                  : 'var(--coffee-color)'
              return (
                <BarGroup key={'bars' + i} className={bar.label + ''}>
                  <rect
                    y={y(bar.label)}
                    width={Math.abs(x(bar.value))}
                    height={barHeight}
                    fill={barColor}
                    strokeWidth={0.1}
                    stroke={'var(--site-background)'}
                    onMouseEnter={() => {
                      setTooltipContent(bar)
                    }}
                    onMouseLeave={() => setTooltipContent(undefined)}
                  />
                </BarGroup>
              )
            })}

          {/*

            OVER MAXIMUM
            
          */}

          <BarGroup key={'barover'}>
            <rect
              y={y(maxLabel) - 35}
              width={Math.abs(x(countOverMaximum))}
              height={barHeight}
              fill={
                userValue > maxLabel
                  ? 'var(--orange-main)'
                  : 'var(--coffee-color)'
              }
              onMouseEnter={() =>
                setTooltipContent({
                  label: maxLabel,
                  value: countOverMaximum
                })
              }
              onMouseLeave={() => setTooltipContent(undefined)}
            />

            <text
              textAnchor='end'
              fill='var(--coffee-light)'
              y={y(maxLabel) - 35 + barHeight}
              x={-10}
              className='over'
            >
              <Labels axisTick>
                {'>'}
                {maxLabel}
              </Labels>
            </text>
          </BarGroup>
          {/*

           Zero
            
          */}
          {
            <BarGroup key={'barzero'}>
              <rect
                y={y.range()[1]}
                width={x(countAbstinent)}
                height={barHeight}
                fill={
                  userValue === 0
                    ? 'var(--orange-main)'
                    : 'var(--coffee-color_2)'
                }
                onMouseEnter={() =>
                  setTooltipContent({
                    label: 0,
                    value: countAbstinent
                  })
                }
                onMouseLeave={() => setTooltipContent(undefined)}
              />
            </BarGroup>
          }
          {/*
            
            User Value
            
            */}
          {!!(userValue || userValue === 0) && (
            <>
              <g
                className='user-value'
                transform={`translate(${-40}, ${
                  userValue > maxLabel
                    ? 10
                    : y(Math.round(userValue / 20) * 20) - 16
                })`}
              >
                <path
                  d='m0,.1c.07,10.89,10.72,19.84,24.74,21.19l-.02,3.36,14.12-3.68h0s-14.07-3.87-14.07-3.87l-.02,3.19C11.23,18.96.99,10.4.99,0Z'
                  fill='var(--orange-main)'
                />
                <rect
                  x={-45}
                  y={-50}
                  width={75}
                  height={50}
                  fill='var(--orange-main)'
                />

                <text
                  textAnchor='end'
                  x={22}
                  y={-28}
                  fill={'var(--site-background)'}
                >
                  <Labels userMarker>{lang === 'de' ? 'Sie' : 'Vous'}</Labels>
                  <Labels
                    userMarker
                    x={22}
                    dy={18}
                    style={{
                      fontWeight: 300,
                      fontSize: '15px',
                      fontFamily: 'var(--font-sans-serif)'
                    }}
                  >
                    {userValue} mg
                  </Labels>
                </text>
              </g>
            </>
          )}
        </g>

        <g transform={`translate(${leftMargin + 5}, 10)`}>
          <g>
            {tooltipContent && (
              <Tooltip
                content={tooltipContent}
                y={y(tooltipContent.label) - barHeight * 1.5}
              />
            )}
          </g>
        </g>
      </svg>
    </BarChartRoot>
  )
}
const Tooltip = ({
  content,

  y
}: {
  content: { label: number; value: number }
  y: number
}) => {
  const Labels = useLabels(labelStyles, 'svg')

  // if(window.innerWidth<711) return null

  return (
    <TooltipRoot transform='translate(0,0)'>
      <rect x={10} y={y} width={120} height={barHeight * 2.5} fill={'#fff'} />
      <text x={15} y={y + 18}>
        <Labels annotation>{content.label}mg</Labels>
      </text>
      <text x={50} y={y + 18} textAnchor='start'>
        <Labels annotationBold dx={10}>
          {content.value}
        </Labels>
      </text>
    </TooltipRoot>
  )
}

const TooltipRoot = styled.g`
  pointer-events: none;
  font-family: var(--font-sans-serif);
  font-size: 15px;
  rect {
    filter: drop-shadow(0px 0px 4px rgb(0 0 0 / 0.4));

    pointer-events: none;
  }
`

const BarGroup = styled.g`
  font-family: var(--font-sans-serif);
  /* text {
    font-size: 11px;
    letter-spacing: 0.2px;
    fill: var(--beige-main);
  } */
  rect {
    &:hover {
      fill: var(--beige-dark-1);
      stroke: var(--beige-dark-1);

      cursor: pointer;
    }
  }
`
const BarChartRoot = styled.div`
  width: 100%;
  position: relative;
  line-height: 0;
  svg {
    overflow: visible;
  }
`
const AverageValue = styled.text`
  font-family: var(--font-sans-serif);
  font-size: 13px;
  font-weight: 400;
  fill: var(--coffee-color);
`
const HealthValue = styled.text`
  font-family: var(--font-sans-serif);
  font-size: 13px;
  font-weight: 400;
  fill: var(--green-main);
`
